/**
 * Ocean Import 그리드 QuickFilter 전용: 행에 평탄화 문자열 `__quick_search`를 붙입니다.
 * 컬럼 renderCell과 동일한 표시 규칙(날짜, 링크 URL, View 플레이스홀더)을 따릅니다.
 */

import { ensureAbsoluteUrl, formatDate, formatLocationCell } from './shipmentMonitoringDefShared';

function pushPart(parts, v) {
    if (v == null) return;
    const s = typeof v === 'string' ? v.trim() : String(v);
    if (s) parts.push(s);
}

function linkDisplayAndUrl(text, href) {
    const rawHref = href != null ? String(href).trim() : '';
    const absoluteUrl = ensureAbsoluteUrl(rawHref);
    const t = text != null ? String(text).trim() : '';
    const display = t || (absoluteUrl ? 'View' : '');
    return { display, absoluteUrl, rawHref };
}

function appendLinkColumnParts(parts, text, href) {
    const { display, absoluteUrl, rawHref } = linkDisplayAndUrl(text, href);
    pushPart(parts, display);
    pushPart(parts, absoluteUrl);
    if (rawHref && rawHref !== absoluteUrl) pushPart(parts, rawHref);
}

const HBL_COLUMN_KEYS = new Set([
    'il_trade_partner_id_shipper',
    'il_trade_partner_id_consignee',
    'hbl',
    'hbl_link',
    'pre_alert',
    'isf',
    'isf_link',
    'hbl_release',
    'arrival_notice',
    'arrival_notice_link',
    'customs',
    'customs_link',
    'invoice',
    'invoice_date',
    'invoice_link',
    'crdr',
    'crdr_date',
    'crdr_link',
]);

const CNTR_COLUMN_KEYS = new Set([
    'cntr_number',
    'volume',
    'il_location_id_deliver_to',
    'pierpass',
    'delivery_order',
    'delivery_order_link',
    'il_trade_partner_id',
    'proof',
    'proof_date',
    'proof_link',
    'empty',
    'empty_date',
]);

/**
 * @param {unknown} val
 * @param {string} key
 * @param {string[]} parts
 * @param {number} depth
 */
function appendGenericValueForSearch(val, key, parts, depth = 0) {
    if (val == null || depth > 8) return;
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        if (/_link$/i.test(key) && typeof val === 'string') {
            appendLinkColumnParts(parts, '', val);
            return;
        }
        if (/_date$/i.test(key)) {
            pushPart(parts, formatDate(val));
            return;
        }
        pushPart(parts, val);
        return;
    }
    if (Array.isArray(val)) {
        val.forEach((item, i) => appendGenericValueForSearch(item, `${key}[${i}]`, parts, depth + 1));
        return;
    }
    if (typeof val === 'object') {
        pushPart(parts, formatLocationCell(val));
        for (const [nk, nv] of Object.entries(val)) {
            appendGenericValueForSearch(nv, nk, parts, depth + 1);
        }
    }
}

function appendRemainingHblOrCntrKeys(obj, handled, parts) {
    if (!obj || typeof obj !== 'object') return;
    for (const [k, v] of Object.entries(obj)) {
        if (handled.has(k)) continue;
        appendGenericValueForSearch(v, k, parts, 0);
    }
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string[]}
 */
function buildOceanImportQuickSearchParts(row) {
    const parts = [];

    for (const [k, v] of Object.entries(row)) {
        if (k === 'il_ocean_import_hbl' || k === 'il_ocean_import_cntr') continue;
        if (k.startsWith('__')) continue;
        if (v == null) continue;
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            pushPart(parts, v);
        }
    }

    pushPart(parts, formatLocationCell(row.il_location_id_pol));
    pushPart(parts, formatLocationCell(row.il_location_id_pod));
    pushPart(parts, formatLocationCell(row.il_location_id_fpod));
    pushPart(parts, formatLocationCell(row.il_opus_vessel_id));

    pushPart(parts, formatDate(row.etd));
    pushPart(parts, formatDate(row.eta));
    pushPart(parts, formatDate(row.feta));

    const hblList = Array.isArray(row.il_ocean_import_hbl) ? row.il_ocean_import_hbl : [];
    for (const hbl of hblList) {
        if (!hbl || typeof hbl !== 'object') continue;
        pushPart(parts, hbl.il_trade_partner_id_shipper);
        pushPart(parts, hbl.il_trade_partner_id_consignee);
        appendLinkColumnParts(parts, hbl.hbl, hbl.hbl_link);
        pushPart(parts, hbl.pre_alert);
        appendLinkColumnParts(parts, hbl.isf, hbl.isf_link);
        pushPart(parts, hbl.hbl_release);
        appendLinkColumnParts(parts, hbl.arrival_notice, hbl.arrival_notice_link);
        appendLinkColumnParts(parts, hbl.customs, hbl.customs_link);
        pushPart(parts, hbl.invoice);
        pushPart(parts, formatDate(hbl.invoice_date));
        appendLinkColumnParts(parts, hbl.invoice, hbl.invoice_link);
        pushPart(parts, hbl.crdr);
        pushPart(parts, formatDate(hbl.crdr_date));
        appendLinkColumnParts(parts, hbl.crdr, hbl.crdr_link);

        appendRemainingHblOrCntrKeys(hbl, HBL_COLUMN_KEYS, parts);
    }

    const cntrList = Array.isArray(row.il_ocean_import_cntr) ? row.il_ocean_import_cntr : [];
    for (const cntr of cntrList) {
        if (!cntr || typeof cntr !== 'object') continue;
        pushPart(parts, cntr.cntr_number);
        pushPart(parts, cntr.volume);
        pushPart(parts, formatLocationCell(cntr.il_location_id_deliver_to));
        pushPart(parts, cntr.pierpass);
        appendLinkColumnParts(parts, cntr.delivery_order, cntr.delivery_order_link);
        pushPart(parts, cntr.il_trade_partner_id);
        pushPart(parts, cntr.proof);
        pushPart(parts, formatDate(cntr.proof_date));
        appendLinkColumnParts(parts, cntr.proof, cntr.proof_link);
        pushPart(parts, cntr.empty);
        pushPart(parts, formatDate(cntr.empty_date));

        appendRemainingHblOrCntrKeys(cntr, CNTR_COLUMN_KEYS, parts);
    }

    pushPart(parts, row.mbl_release);
    pushPart(parts, row.ocean_freight);
    pushPart(parts, formatDate(row.paid_on));
    const ofLink = row.ocean_freight_link != null ? String(row.ocean_freight_link).trim() : '';
    if (ofLink) {
        pushPart(parts, ofLink);
        pushPart(parts, ensureAbsoluteUrl(ofLink));
    }

    return parts;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {Record<string, unknown>}
 */
export function attachOceanImportQuickSearchFields(row) {
    const parts = buildOceanImportQuickSearchParts(row);
    const __quick_search = parts.join(' ');
    return { ...row, __quick_search };
}
